import {
  MessageContext,
  type OnboardingMessage,
} from "@/lib/api/onboarding-messages";
import {
  useCreateMessage,
  useDeleteMessage,
  useMessages,
  useReorderMessages,
} from "@/lib/hooks/onboarding-messages/use-onboarding-messages";
import { Button, IconButton } from "@somethings-ui/web";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, GripVertical, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { onboardingMessagesForDisplay } from "./first-moments-summaries";

interface FirstMomentsMessagesProps {
  userId: string;
  context: MessageContext;
}

export function FirstMomentsMessages({
  userId,
  context,
}: FirstMomentsMessagesProps) {
  const [newMessage, setNewMessage] = useState("");
  const { data: messages, isLoading, isSuccess } = useMessages(userId, context);
  const reorderMessages = useReorderMessages(userId, context);
  const createMessage = useCreateMessage(userId, context);
  const deleteMessage = useDeleteMessage(userId, context);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const display = useMemo(
    () =>
      onboardingMessagesForDisplay(
        isSuccess ? (messages ?? []) : undefined,
        context,
      ),
    [context, isSuccess, messages],
  );
  const textMessages = display.messages;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = textMessages.findIndex((msg) => msg.id === active.id);
    const newIndex = textMessages.findIndex((msg) => msg.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    reorderMessages.mutate(
      arrayMove(textMessages, oldIndex, newIndex).map((msg) => msg.id),
    );
  };

  const handleMove = (index: number, delta: number) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= textMessages.length) return;
    reorderMessages.mutate(
      arrayMove(textMessages, index, nextIndex).map((msg) => msg.id),
    );
  };

  const canAdd = Boolean(newMessage.trim()) && !createMessage.isPending;

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-text-secondary" />
        </div>
      ) : display.usingDefault ? (
        <div className="rounded-[17px] border border-[#dfddd8] bg-white px-3 py-2.5">
          <p className="font-body text-xs font-medium leading-4 text-[#6e6b67]">
            Default. Adding a message replaces this.
          </p>
          <p className="mt-1 font-body text-sm leading-5 text-text-primary">
            {textMessages[0]?.message}
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={textMessages.map((msg) => msg.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-2">
              {textMessages.map((message, index) => (
                <FirstMomentsMessageRow
                  key={message.id}
                  message={message}
                  isFirst={index === 0}
                  isLast={index === textMessages.length - 1}
                  onMoveUp={() => handleMove(index, -1)}
                  onMoveDown={() => handleMove(index, 1)}
                  onDelete={() => deleteMessage.mutate(message.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <Textarea
        value={newMessage}
        onChange={(event) => setNewMessage(event.target.value)}
        placeholder="hey! so glad you reached out..."
        className="h-[120px] rounded-xl border-[#8a8275] px-6 py-4 font-body text-base placeholder:text-[#a0998d]"
      />
      <div className="flex h-20 items-center pb-2 pt-4">
        <Button
          size="lg"
          fullWidth={false}
          className="h-14 min-w-[120px]"
          disabled={!canAdd}
          loading={createMessage.isPending}
          onPress={() => {
            createMessage.mutate({ message: newMessage.trim() });
            setNewMessage("");
          }}
        >
          Add message
        </Button>
      </div>
    </div>
  );
}

function FirstMomentsMessageRow({
  message,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  message: OnboardingMessage;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: message.id });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="flex items-start gap-2 rounded-[17px] border border-[#dfddd8] bg-white px-3 py-2.5"
    >
      <IconButton
        aria-label="Reorder message"
        variant="header-ghost"
        size="sm"
        className="mt-1 cursor-grab touch-none"
        icon={<GripVertical className="size-4" />}
        {...attributes}
        {...listeners}
      />
      <p className="min-w-0 flex-1 font-body text-sm leading-5 text-text-primary">
        {message.message}
      </p>
      <div className="flex shrink-0 items-center">
        <IconButton
          aria-label="Move up"
          variant="header-ghost"
          size="sm"
          disabled={isFirst}
          icon={<ArrowUp className="size-4" />}
          onClick={onMoveUp}
        />
        <IconButton
          aria-label="Move down"
          variant="header-ghost"
          size="sm"
          disabled={isLast}
          icon={<ArrowDown className="size-4" />}
          onClick={onMoveDown}
        />
        <IconButton
          aria-label="Delete"
          variant="header-ghost"
          size="sm"
          icon={<X className="size-4" />}
          onClick={onDelete}
        />
      </div>
    </li>
  );
}
